import { Component, inject, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-privacy',
  standalone: true,
  imports: [RouterModule],
  template: `
    <div class="legal-page">
      <header class="legal-header">
        <div class="legal-header-inner">
          <a routerLink="/" class="header-brand">
            K<span class="brand-accent">ΛI</span>ZENIC
          </a>
          <a routerLink="/" class="back-link">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back
          </a>
        </div>
      </header>

      <main class="legal-main">
        <div class="legal-title-section">
          <h1>Privacy Policy</h1>
          <p class="legal-meta">
            <strong>Last Updated:</strong> August 4, 2025
          </p>
        </div>

        <section id="introduction" class="policy-section">
          <h2>1. Introduction</h2>
          <p>
            Welcome to Kaizenic ("we," "our," or "us"). Kaizenic is an
            AI-powered personalized learning platform featuring K<span
              class="brand-accent"
              >ΛI</span
            >, your intelligent learning co-navigator. Our platform helps users
            define learning aspirations, receive hyper-personalized learning
            paths, engage in interactive AI-guided learning experiences, and
            track their progress toward mastery.
          </p>
          <p>
            K<span class="brand-accent">ΛI</span> serves as your personal AI
            learning companion, crafting bespoke learning modules, engaging in
            Socratic dialogue, providing real-time feedback, and adapting to
            your unique learning style and pace.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, disclose, and
            safeguard your information when you use our platform, whether
            accessed via web browser, mobile application, or other digital
            services (collectively, the "Service").
          </p>
          <p>
            By using our Service, you agree to the collection and use of
            information in accordance with this Privacy Policy. If you do not
            agree with our policies and practices, please do not use our
            Service.
          </p>
        </section>

        <section id="information-collection" class="policy-section">
          <h2>2. Information We Collect</h2>

          <h3>2.1 Personal Information</h3>
          <p>
            We collect information that identifies, relates to, or could
            reasonably be linked with you ("Personal Information"), including:
          </p>
          <ul>
            <li>
              <strong>Account Information:</strong> Name, email address,
              username, password, profile photo
            </li>
            <li>
              <strong>Contact Information:</strong> Phone number, mailing
              address (optional)
            </li>
            <li>
              <strong>Payment Information:</strong> Billing details,
              subscription preferences (processed securely through third-party
              payment processors)
            </li>
            <li>
              <strong>Profile Data:</strong> Educational background,
              professional information, learning interests
            </li>
          </ul>

          <h3>2.2 Learning and AI Interaction Data</h3>
          <p>
            To provide personalized learning experiences through K<span
              class="brand-accent"
              >ΛI</span
            >, we collect:
          </p>
          <ul>
            <li>
              <strong>Learning Aspirations:</strong> Your defined learning
              goals, target skills, desired outcomes, preferred learning
              timelines, and personal motivations
            </li>
            <li>
              <strong>AI Conversations:</strong> All interactions with K<span
                class="brand-accent"
                >ΛI</span
              >, including questions asked, responses provided, Socratic
              dialogue exchanges, clarification requests, and feedback
            </li>
            <li>
              <strong>Learning Progress:</strong> Module completion rates, quiz
              scores, time spent on materials, achievement milestones, skill
              progression, and mastery indicators
            </li>
            <li>
              <strong>Learning Preferences:</strong> Content format preferences
              (text, video, audio, interactive exercises), learning pace,
              difficulty level preferences, and engagement patterns
            </li>
            <li>
              <strong>Skill Competencies:</strong> Self-reported skill levels,
              competency assessments, knowledge gap identification, and skill
              advancement tracking
            </li>
            <li>
              <strong>Learning Path Data:</strong> Personalized curricula
              created by K<span class="brand-accent">ΛI</span>, module
              sequences, adaptive adjustments, and path modifications based on
              your progress
            </li>
            <li>
              <strong>Engagement Analytics:</strong> Learning session patterns,
              time-on-task metrics, interaction frequency with K<span
                class="brand-accent"
                >ΛI</span
              >, resource utilization, and learning behavior patterns
            </li>
            <li>
              <strong>Content Interactions:</strong> Resource ratings, bookmark
              activities, note-taking patterns, and content preferences
            </li>
          </ul>

          <h3>2.3 Technical Information</h3>
          <p>
            We automatically collect certain information to help our Service
            work properly:
          </p>
          <ul>
            <li>
              <strong>Device Information:</strong> Your internet address, web
              browser type, device type
            </li>
            <li>
              <strong>Usage Data:</strong> Which pages you visit, how long you
              spend learning, when you use our Service
            </li>
            <li>
              <strong>Performance Data:</strong> How well our Service works on
              your device
            </li>
          </ul>

          <h3>2.4 Connected Services and External Learning Data</h3>
          <p>
            When you choose to connect other services to enhance your learning
            experience with K<span class="brand-accent">ΛI</span>:
          </p>
          <ul>
            <li>
              <strong>Educational Platforms:</strong> Content from connected
              learning platforms, courses, or educational resources
            </li>
            <li>
              <strong>Calendar Integration:</strong> Schedule information to
              help K<span class="brand-accent">ΛI</span> optimize learning
              session timing
            </li>
            <li>
              <strong>Professional Profiles:</strong> LinkedIn or other
              professional platform data to better understand your career
              context
            </li>
            <li>
              <strong>Learning Communities:</strong> If you participate in
              learning communities, your shared achievements and peer
              interactions
            </li>
            <li>
              <strong>External Content:</strong> Web articles, videos, or other
              learning materials you share with K<span class="brand-accent"
                >ΛI</span
              >
            </li>
          </ul>
        </section>

        <section id="information-use" class="policy-section">
          <h2>3. How We Use Your Information</h2>

          <h3>3.1 Core AI-Powered Learning Functionality</h3>
          <ul>
            <li>
              <strong>Hyper-Personalized Learning Paths:</strong> K<span
                class="brand-accent"
                >ΛI</span
              >
              creates completely customized curricula based on your goals,
              learning style, preferences, progress, and competency assessments
            </li>
            <li>
              <strong>AI Learning Companion:</strong> K<span
                class="brand-accent"
                >ΛI</span
              >
              provides Socratic dialogue, interactive guidance, real-time
              feedback, and on-demand clarification
            </li>
            <li>
              <strong>Adaptive Content Generation:</strong> Create bespoke
              learning modules with text, multimedia content, interactive
              exercises, and assessments
            </li>
            <li>
              <strong>Intelligent Progress Tracking:</strong> Monitor your
              advancement with AI-driven insights and competency mapping
            </li>
            <li>
              <strong>Dynamic Goal Management:</strong> Help you articulate,
              refine, track, and achieve learning aspirations
            </li>
            <li>
              <strong>Contextual Resource Curation:</strong> K<span
                class="brand-accent"
                >ΛI</span
              >
              proactively suggests and integrates relevant external resources
            </li>
          </ul>

          <h3>3.2 Service Improvement and Analytics</h3>
          <ul>
            <li>
              <strong>Platform Optimization:</strong> Analyze usage patterns to
              improve user experience and functionality
            </li>
            <li>
              <strong>AI Model Training:</strong> Enhance K<span
                class="brand-accent"
                >ΛI</span
              >'s capabilities (using anonymized and aggregated data only)
            </li>
            <li>
              <strong>Feature Development:</strong> Develop new features based
              on user needs and behavior
            </li>
            <li>
              <strong>Quality Assurance:</strong> Monitor service performance
              and identify technical issues
            </li>
          </ul>

          <h3>3.3 Communication and Support</h3>
          <ul>
            <li>
              <strong>Customer Support:</strong> Respond to inquiries,
              troubleshoot issues, and provide assistance
            </li>
            <li>
              <strong>Service Communications:</strong> Send important updates,
              security notices, and policy changes
            </li>
            <li>
              <strong>Marketing Communications:</strong> Share educational
              content, feature updates, and promotional offers (with your
              consent)
            </li>
          </ul>

          <h3>3.4 Legal and Security Purposes</h3>
          <ul>
            <li>
              <strong>Compliance:</strong> Meet legal obligations and regulatory
              requirements
            </li>
            <li>
              <strong>Security:</strong> Detect, prevent, and respond to fraud,
              abuse, and security threats
            </li>
            <li>
              <strong>Enforcement:</strong> Enforce our Terms of Service and
              protect our rights and property
            </li>
          </ul>
        </section>

        <section id="data-sharing" class="policy-section">
          <h2>4. Data Sharing and Disclosure</h2>

          <h3>4.1 Service Partners</h3>
          <p>
            We work with trusted companies that help us provide our Service:
          </p>
          <ul>
            <li>
              <strong>AI Technology Partners:</strong> Companies that help
              K<span class="brand-accent">ΛI</span> understand and respond to
              your questions
            </li>
            <li>
              <strong>Cloud Storage Providers:</strong> Secure data centers that
              store your information safely
            </li>
            <li>
              <strong>Analytics Services:</strong> Companies that help us
              understand how people use our Service
            </li>
            <li>
              <strong>Payment Companies:</strong> Secure services that process
              your subscription payments
            </li>
            <li>
              <strong>Communication Tools:</strong> Services that help us send
              you emails and notifications
            </li>
            <li>
              <strong>Content Delivery:</strong> Services that help deliver
              learning materials to you quickly
            </li>
          </ul>
          <p>
            All our partners are required to keep your data safe and only use it
            for the specific purposes we've agreed upon.
          </p>

          <h3>4.2 Legal Requirements</h3>
          <p>We may disclose your information when required by law or to:</p>
          <ul>
            <li>
              Comply with legal processes, court orders, or government requests
            </li>
            <li>Enforce our Terms of Service or other agreements</li>
            <li>
              Protect the rights, property, or safety of Kaizenic, our users, or
              the public
            </li>
            <li>Prevent fraud, abuse, or illegal activities</li>
          </ul>

          <h3>4.3 Business Transfers</h3>
          <p>
            In the event of a merger, acquisition, or sale of assets, your
            information may be transferred to the new entity, subject to the
            same privacy protections.
          </p>

          <h3>4.4 Public Learning Profiles</h3>
          <p>
            If you choose to create a public learning profile, certain
            information may be visible to other users:
          </p>
          <ul>
            <li>Username and profile photo</li>
            <li>Learning achievements and badges</li>
            <li>Completed learning paths (unless marked private)</li>
            <li>Public skill assessments and certifications</li>
          </ul>
        </section>

        <section id="cookies" class="policy-section">
          <h2>5. Cookies and Tracking Technologies</h2>

          <h3>5.1 Types of Cookies We Use</h3>
          <ul>
            <li>
              <strong>Essential Cookies:</strong> Required for basic Service
              functionality and security
            </li>
            <li>
              <strong>Performance Cookies:</strong> Help us understand how you
              use our Service to improve performance
            </li>
            <li>
              <strong>Functional Cookies:</strong> Remember your preferences and
              personalize your experience
            </li>
            <li>
              <strong>Analytics Cookies:</strong> Collect information about
              Service usage for analysis and improvement
            </li>
          </ul>

          <h3>5.2 Cookie Management</h3>
          <p>
            You can control cookie preferences through your browser settings.
            However, disabling certain cookies may limit Service functionality.
          </p>

          <h3>5.3 Website Analytics</h3>
          <p>
            We use analytics tools to understand how people use our Service,
            which helps us make improvements. These tools may use cookies and
            collect data according to their own privacy policies.
          </p>
        </section>

        <section id="security" class="policy-section">
          <h2>6. Data Security</h2>

          <h3>6.1 Security Measures</h3>
          <p>
            We use industry-standard security measures to protect your
            information:
          </p>
          <ul>
            <li>
              <strong>Data Encryption:</strong> Your information is scrambled
              when sent to us and when stored on our servers
            </li>
            <li>
              <strong>Access Controls:</strong> Only authorized team members can
              access your data, and they must use secure login methods
            </li>
            <li>
              <strong>Security Monitoring:</strong> We continuously monitor our
              systems for potential threats
            </li>
            <li>
              <strong>Regular Security Reviews:</strong> We regularly test our
              security systems and fix any issues we find
            </li>
            <li>
              <strong>Data Protection:</strong> We only collect information we
              actually need and delete it when we no longer need it
            </li>
          </ul>

          <h3>6.2 Data Breach Response</h3>
          <p>In the unlikely event of a data breach, we will:</p>
          <ul>
            <li>Notify affected users within 72 hours</li>
            <li>Report to relevant authorities as required by law</li>
            <li>
              Take immediate steps to secure the breach and prevent further
              unauthorized access
            </li>
            <li>
              Provide regular updates on our investigation and remediation
              efforts
            </li>
          </ul>
        </section>

        <section id="retention" class="policy-section">
          <h2>7. Data Retention</h2>

          <h3>7.1 How Long We Keep Your Data</h3>
          <ul>
            <li>
              <strong>Account Information:</strong> Kept while your account is
              active, plus 30 days after you delete it
            </li>
            <li>
              <strong>Learning Data and AI Conversations:</strong> Your learning
              paths, progress, goals, and conversations with K<span
                class="brand-accent"
                >ΛI</span
              >
              are kept for 3 years
            </li>
            <li>
              <strong>Competency and Skill Data:</strong> Skill assessments,
              competency tracking, and mastery indicators kept for 5 years
            </li>
            <li>
              <strong>Usage Analytics:</strong> Anonymous and aggregated usage
              patterns may be kept for up to 5 years
            </li>
            <li>
              <strong>Customer Support:</strong> Records of our conversations
              kept for 2 years
            </li>
            <li>
              <strong>Payment Information:</strong> Kept as required by law
              (typically 7 years for tax and accounting purposes)
            </li>
            <li>
              <strong>AI Training Data:</strong> Anonymized and aggregated
              learning patterns may be retained indefinitely, but never in a way
              that can identify individual users
            </li>
          </ul>

          <h3>7.2 Data Deletion</h3>
          <p>
            When data reaches the end of its retention period, we securely
            delete it using industry-standard methods that make recovery
            impossible.
          </p>
        </section>

        <section id="privacy-rights" class="policy-section">
          <h2>8. Your Privacy Rights</h2>

          <h3>8.1 Access and Portability</h3>
          <ul>
            <li>
              <strong>Access:</strong> Request a copy of your personal data in a
              structured, machine-readable format
            </li>
            <li>
              <strong>Portability:</strong> Download your learning data, goals,
              and progress information
            </li>
          </ul>

          <h3>8.2 Correction and Updates</h3>
          <ul>
            <li>
              <strong>Update:</strong> Modify your personal information and
              preferences through your account settings
            </li>
            <li>
              <strong>Correction:</strong> Request correction of inaccurate or
              incomplete data
            </li>
          </ul>

          <h3>8.3 Deletion and Restriction</h3>
          <ul>
            <li>
              <strong>Delete Account:</strong> Permanently delete your account
              and associated data
            </li>
            <li>
              <strong>Selective Deletion:</strong> Delete specific types of data
              while maintaining your account
            </li>
            <li>
              <strong>Restrict Processing:</strong> Limit how we use your data
              in certain circumstances
            </li>
          </ul>

          <h3>8.4 Consent Management</h3>
          <ul>
            <li>
              <strong>Withdraw Consent:</strong> Opt out of marketing
              communications and optional data processing
            </li>
            <li>
              <strong>Cookie Preferences:</strong> Manage cookie settings
              through our preference center
            </li>
          </ul>

          <h3>8.5 Objection Rights</h3>
          <ul>
            <li>
              <strong>Object to Processing:</strong> Object to processing of
              your data for certain purposes
            </li>
            <li>
              <strong>Automated Decision-Making:</strong> Request human review
              of AI-driven decisions that significantly affect you
            </li>
          </ul>
        </section>

        <section id="regional-rights" class="policy-section">
          <h2>9. Regional Privacy Rights</h2>

          <h3>9.1 GDPR (European Union)</h3>
          <p>
            If you are located in the EU, you have additional rights under GDPR:
          </p>
          <ul>
            <li>Right to be forgotten</li>
            <li>Right to data portability</li>
            <li>Right to object to processing</li>
            <li>Right not to be subject to automated decision-making</li>
            <li>Right to lodge a complaint with supervisory authorities</li>
          </ul>
          <p><strong>Legal Basis for Processing:</strong></p>
          <ul>
            <li>Contract performance (providing the Service)</li>
            <li>Legitimate interests (improving the Service, security)</li>
            <li>Consent (marketing communications, optional features)</li>
          </ul>

          <h3>9.2 CCPA (California)</h3>
          <p>California residents have specific rights under CCPA:</p>
          <ul>
            <li>Right to know what personal information is collected</li>
            <li>Right to delete personal information</li>
            <li>
              Right to opt-out of the sale of personal information (Note: We do
              not sell personal information)
            </li>
            <li>Right to non-discrimination for exercising privacy rights</li>
          </ul>

          <h3>9.3 Other Jurisdictions</h3>
          <p>
            We comply with applicable privacy laws in all jurisdictions where we
            operate.
          </p>
        </section>

        <section id="children" class="policy-section">
          <h2>10. Children's Privacy</h2>
          <p>
            Kaizenic is not intended for children under 13 years of age. We do
            not knowingly collect personal information from children under 13.
            If we discover that we have collected information from a child under
            13, we will promptly delete such information.
          </p>
          <p>
            For users between 13-18 years old, parental consent may be required
            in certain jurisdictions.
          </p>
        </section>

        <section id="international" class="policy-section">
          <h2>11. International Data Transfers</h2>
          <p>
            Your information may be stored and processed in countries outside
            where you live, including the United States. When this happens, we
            make sure your data is still protected by:
          </p>
          <ul>
            <li>
              Only transferring data to countries with strong data protection
              laws
            </li>
            <li>
              Using legal agreements that require the same level of protection
            </li>
            <li>
              Following international frameworks for secure data transfers
            </li>
          </ul>
        </section>

        <section id="trial-subscription" class="policy-section">
          <h2>12. Free Trial and Subscription Data</h2>

          <h3>12.1 7-Day Free Trial</h3>
          <p>During your free trial:</p>
          <ul>
            <li>
              We collect the same types of data as described in this policy
            </li>
            <li>You can cancel anytime without charge</li>
            <li>
              Data collected during the trial is subject to the same protections
            </li>
          </ul>

          <h3>12.2 Subscription Data</h3>
          <p>For paid subscriptions:</p>
          <ul>
            <li>
              Payment information is processed securely by third-party
              processors
            </li>
            <li>
              Subscription preferences and billing history are maintained for
              account management
            </li>
            <li>Cancellation can be done through your account settings</li>
          </ul>
        </section>

        <section id="policy-updates" class="policy-section">
          <h2>13. Updates to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. When we do:
          </p>
          <ul>
            <li>
              <strong>Notification:</strong> We will notify you of material
              changes via email or prominent notice in the Service
            </li>
            <li>
              <strong>Effective Date:</strong> Changes become effective 30 days
              after notification
            </li>
            <li>
              <strong>Continued Use:</strong> Your continued use of the Service
              after the effective date constitutes acceptance of the updated
              policy
            </li>
          </ul>
        </section>

        <section id="contact" class="policy-section">
          <h2>14. Contact Information</h2>
          <p>
            For all privacy-related inquiries, including questions about this
            Privacy Policy, data practices, GDPR requests, and security issues:
          </p>
          <p>
            <strong>Email:</strong>
            <a href="mailto:privacy&#64;kaizenic.ai">privacy&#64;kaizenic.ai</a
            ><br />
            <strong>Response Time:</strong> We respond to privacy inquiries
            within 5 business days
          </p>
          <p><strong>When contacting us, please include:</strong></p>
          <ul>
            <li>Clear subject line indicating the type of request</li>
            <li>Your account email address (if applicable)</li>
            <li>Specific details about your request or concern</li>
          </ul>
        </section>

        <section id="additional" class="policy-section">
          <h2>15. Additional Information</h2>

          <h3>15.1 Third-Party Links</h3>
          <p>
            Our Service may contain links to third-party websites or services.
            We are not responsible for the privacy practices of these external
            sites.
          </p>

          <h3>
            15.2 AI and Machine Learning with K<span class="brand-accent"
              >ΛI</span
            >
          </h3>
          <p>
            K<span class="brand-accent">ΛI</span> uses advanced machine learning
            and natural language processing to provide hyper-personalized
            learning experiences. K<span class="brand-accent">ΛI</span> operates
            with transparency, explaining its reasoning and recommendations. You
            can request explanations for AI-driven decisions and have the right
            to human review of any significant automated decisions.
          </p>

          <h3>15.3 Research and Development</h3>
          <p>
            We may use anonymized and aggregated data for research purposes to
            advance learning science and improve AI capabilities in education.
          </p>

          <h3>15.4 Transparency Reporting</h3>
          <p>
            We are committed to transparency about government requests for user
            data and will publish annual transparency reports.
          </p>
        </section>

        <footer class="policy-footer">
          <p>
            <strong>Last Updated:</strong> August 4, 2025 &nbsp;·&nbsp;
            <strong>Version:</strong> 1.0
          </p>
          <p>
            For the most current version of this Privacy Policy, please visit:
            <a href="https://kaizenic.ai/privacy">kaizenic.ai/privacy</a>
          </p>
        </footer>
      </main>
    </div>
  `,
  styleUrl: './privacy.component.scss',
})
export class PrivacyComponent implements OnInit {
  private readonly platformId = inject(PLATFORM_ID);

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo(0, 0);
    }
  }
}
